import { useCallback, useReducer } from 'react';
import type { ChatOrderProduct } from '../ChatOrderProductCandidatesModal';
import { chatOrderReducer, initialState } from '../logic/chatOrderReducer';
import * as selectors from '../logic/chatOrderSelectors';

export type { SelectionItem, ProductDeliveryState, SelectionState } from '../logic/chatOrderReducer';

function useChatOrderSelection(products: ChatOrderProduct[]) {
  const [state, dispatch] = useReducer(chatOrderReducer, initialState);

  const productById = new Map(products.map((p) => [p._id, p]));

  const feeByProduct = Object.entries(state.deliveryByProduct)
    .filter(([, d]) => d.includeDeliveryFee)
    .map(([pid, d]) => productById.get(pid)?.deliveryFeeOptions[d.selectedDeliveryFeeOptionIndex]?.fee.amount ?? 0);

  const deliveryFeeTotal = feeByProduct.reduce((sum, fee) => sum + fee, 0);
  const totalPrice = selectors.getItemsSubtotal(state) + deliveryFeeTotal;
  const deliveryFeeCount = selectors.getDeliveryFeeCount(state);

  const toggleItem = useCallback((params: { productId: string; optionId: string | null; price: number }) => {
    dispatch({ type: 'TOGGLE_ITEM', ...params });
  }, []);

  const increaseQuantity = useCallback((productId: string, optionId: string | null) => {
    dispatch({ type: 'INCREASE_QUANTITY', productId, optionId });
  }, []);

  const decreaseQuantity = useCallback((productId: string, optionId: string | null) => {
    dispatch({ type: 'DECREASE_QUANTITY', productId, optionId });
  }, []);

  const toggleDeliveryFee = useCallback((productId: string) => {
    dispatch({ type: 'TOGGLE_DELIVERY_FEE', productId });
  }, []);

  const selectDeliveryFeeOption = useCallback((productId: string, index: number) => {
    dispatch({ type: 'SELECT_DELIVERY_FEE_OPTION', productId, index });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return {
    state,
    totalPrice,
    deliveryFeeTotal,
    deliveryFeeCount,
    reset,
    toggleItem,
    increaseQuantity,
    decreaseQuantity,
    toggleDeliveryFee,
    selectDeliveryFeeOption,
    isItemSelected: (productId: string, optionId: string | null) =>
      selectors.isOrderItemSelected(state, productId, optionId),
    getItemQuantity: (productId: string, optionId: string | null) =>
      selectors.getSelectedItemQuantity(state, productId, optionId),
    getProductDeliveryState: (productId: string) => selectors.getProductDeliveryState(state, productId),
    isProductSelected: (productId: string) => selectors.isProductSelected(state, productId),
    isSubmitDisabled: selectors.isSubmitDisabled(state),
  };
}

export default useChatOrderSelection;
